import React from 'react';

/**
 * Sendet einen Fehler an den Monemee Error-Logging-Endpoint.
 * Fire-and-forget, wirft nie.
 */
export const reportError = (payload) => {
  try {
    fetch('/api/v1/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // keepalive damit der Request auch bei Page-Unload noch ankommt
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
};

/**
 * ErrorBoundary – fängt React-Render-Fehler auf.
 * Wird in index.js um die gesamte App gewrapped.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    reportError({
      level: 'critical',
      message: error?.message || String(error),
      stack: error?.stack || null,
      path: window.location.pathname,
      context: {
        componentStack: info?.componentStack?.slice(0, 1000) || null,
        type: 'react_render',
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '24px',
          fontFamily: 'system-ui, sans-serif',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</p>
          <h1 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: '#111' }}>
            Etwas ist schiefgelaufen
          </h1>
          <p style={{ color: '#666', marginBottom: '24px', maxWidth: '360px' }}>
            Ein unerwarteter Fehler ist aufgetreten. Bitte lade die Seite neu.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              background: '#1E3A8A',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            Seite neu laden
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Richtet globale Error-Handler ein:
 * - window.onerror        → JS-Laufzeitfehler
 * - unhandledrejection    → nicht behandelte Promises
 *
 * Einmalig aufrufen (in index.js).
 */
export const initGlobalErrorHandlers = () => {
  window.onerror = (message, source, lineno, colno, error) => {
    // Ignoriere externe Script-Fehler (z.B. Browser-Extensions)
    if (message === 'Script error.' && !source) return false;

    reportError({
      level: 'error',
      message: String(message),
      stack: error?.stack || null,
      path: window.location.pathname,
      context: {
        source,
        lineno,
        colno,
        type: 'window_onerror',
      },
    });
    return false; // Nicht unterdrücken
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      level: 'error',
      message: reason?.message || String(reason) || 'Unhandled Promise Rejection',
      stack: reason?.stack || null,
      path: window.location.pathname,
      context: {
        type: 'unhandled_promise',
      },
    });
  });
};

export default ErrorBoundary;
